/**
 * Parameter class for Monte Carlo simulation
 * Uses Box-Muller transform for generating normally distributed random numbers
 */
export default class Parameter {
  private mean: number;
  private standardDeviation: number;
  private _cachedZ1: number | null = null;

  constructor(mean: number, standardDeviation: number) {
    this.mean = mean;
    this.standardDeviation = standardDeviation;
  }

  /**
   * Generate a random value from the normal distribution
   * @returns A normally distributed random number
   */
  get(): number {
    // If standard deviation is 0, always return the mean
    if (this.standardDeviation === 0) {
      return this.mean;
    }

    // If we have a cached value, use it
    if (this._cachedZ1 !== null) {
      const result = this.mean + this.standardDeviation * this._cachedZ1;
      this._cachedZ1 = null; // Clear the cache
      return result;
    }

    // Otherwise, generate two new values using Box-Muller
    let u = 0, v = 0;
    while (u === 0) u = Math.random(); // Converting [0,1) to (0,1)
    while (v === 0) v = Math.random();
    
    const R = Math.sqrt(-2.0 * Math.log(u));
    const theta = 2.0 * Math.PI * v;
    const z0 = R * Math.cos(theta);
    const z1 = R * Math.sin(theta);

    // Cache z1 for the next call
    this._cachedZ1 = z1;

    // Return the result based on z0 for this call
    return this.mean + this.standardDeviation * z0;
  }

  /**
   * Get the mean value
   */
  getMean(): number {
    return this.mean;
  }

  /**
   * Get the standard deviation
   */
  getStandardDeviation(): number {
    return this.standardDeviation;
  }

  /**
   * Update the parameters
   */
  setParameters(mean: number, standardDeviation: number): void {
    this.mean = mean;
    this.standardDeviation = standardDeviation;
    this._cachedZ1 = null; // Clear any cached value
  }
}
